import type { components } from "@/api-schema";
import {
  CustomerAddressType,
  CustomerType,
  VehicleAccidentState,
  VehicleBuyInSignatureDevice,
  VehicleBuyInType,
} from "@/api-schema-enums";
import type { VehicleBuyInCreateBody } from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-create.mutation";
import {
  dateToInputString,
  inputStringToDate,
  monthInputStringToApi,
  parseApiDateOnly,
} from "@/common/helpers/date-input";
import { formatDateToApi } from "@/common/helpers/format-date";

type VehicleValuation = components["schemas"]["VehicleValuation"];
type Vehicle = components["schemas"]["Vehicle"];
type CreateData = components["schemas"]["VehicleBuyInCreateDataDto"];
export type VehicleBuyInCustomer = components["schemas"]["Customer"];
type VehicleBuyInCustomerIdentifier = NonNullable<VehicleBuyInCustomer["customerIdentifiers"]>[number];

export function isValidVehicleBuyInCustomerNumber(value: unknown): value is string {
  return typeof value === "string" && /^\d+$/.test(value);
}

export function filterValidVehicleBuyInCustomerNumberIdentifiers(
  identifiers: VehicleBuyInCustomerIdentifier[],
): VehicleBuyInCustomerIdentifier[] {
  return identifiers.filter(({ identifier }) => isValidVehicleBuyInCustomerNumber(identifier));
}

export interface VehicleBuyInFormValues {
  vehicleValuationUuid: string;
  branchUuid: string;
  signatureDevice: VehicleBuyInSignatureDevice | "";
  type: VehicleBuyInType | "";
  tradeInReference: string;
  branch: {
    name: string;
    companyName: string;
    signatureTabletEmail: string;
    address: { street: string; houseNumber: string; postalCode: string; city: string; country: string };
  };
  employee: { firstName: string; lastName: string; email: string; occupation: string };
  customer: {
    type: CustomerType;
    companyName: string;
    registrationNumber: string;
    customerNumber: string;
    firstName: string;
    lastName: string;
    birthDate: string;
    email: string;
    phoneNumber: string;
    address: {
      street: string;
      houseNumber: string;
      houseExtension: string;
      postalCode: string;
      city: string;
      country: string;
    };
  };
  vehicle: {
    vin: string;
    licensePlate: string;
    model: {
      brandName: string;
      modelFamilyName: string;
      modelGenerationName: string;
      modelDescription: string;
    };
    engineKilowatts: string;
    color: string;
    manufacturerColor: string;
    vatDeductible: boolean;
    reimport: boolean;
    numberOfDoors: string;
    registrationDate: string;
    numberOfHolders: string;
    usage: string;
    currentMileage: string;
    huDate: string;
    serviceHistory: string;
    accident: {
      state: VehicleAccidentState;
      description: string;
      value: string;
    };
    exchangeEngineInstalled: boolean;
    exchangeEngine: {
      exchangeDate: string;
      vehicleMileageAtExchange: string;
      engineMileageAtExchange: string;
      notes: string;
    };
  };
  purchase: {
    transferDate: string;
    price: string;
    mileageAtBuyIn: string;
    otherAgreements: string;
  };
}

function numberToString(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : String(value);
}

function apiDateToInputString(value: string | null | undefined): string {
  return dateToInputString(parseApiDateOnly(value));
}

function branchWithoutUuid(branch?: CreateData["branches"][number]) {
  return {
    name: branch?.name ?? "",
    companyName: branch?.companyName ?? "",
    signatureTabletEmail: branch?.signatureTabletEmail ?? "",
    address: {
      street: branch?.address.street ?? "",
      houseNumber: branch?.address.houseNumber ?? "",
      postalCode: branch?.address.postalCode ?? "",
      city: branch?.address.city ?? "",
      country: branch?.address.country ?? "",
    },
  };
}

export function getVehicleBuyInFormDefaults({
  vehicleValuationUuid,
  valuation,
  vehicle,
  createData,
}: {
  vehicleValuationUuid: string;
  valuation: VehicleValuation;
  vehicle: Vehicle;
  createData: CreateData;
}): VehicleBuyInFormValues {
  const defaultBranchUuid = createData.defaultBranchUuid ?? createData.branches[0]?.uuid ?? "";
  const defaultBranch = createData.branches.find((branch) => branch.uuid === defaultBranchUuid);
  const exchangeEngine = vehicle.exchangeEngine;

  return {
    vehicleValuationUuid,
    branchUuid: defaultBranchUuid,
    signatureDevice: "",
    type: "",
    tradeInReference: "",
    branch: branchWithoutUuid(defaultBranch),
    employee: { ...createData.employee },
    customer: {
      type: CustomerType.Private,
      companyName: "",
      registrationNumber: "",
      customerNumber: "",
      firstName: "",
      lastName: "",
      birthDate: "",
      email: "",
      phoneNumber: "",
      address: { street: "", houseNumber: "", houseExtension: "", postalCode: "", city: "", country: "DE" },
    },
    vehicle: {
      vin: valuation.vinUsed ?? vehicle.vin ?? "",
      licensePlate: vehicle.licensePlate ?? "",
      model: {
        brandName: vehicle.modelGeneration?.modelFamily?.brand?.name ?? "",
        modelFamilyName: vehicle.modelGeneration?.modelFamily?.name ?? "",
        modelGenerationName: vehicle.modelGeneration?.name ?? "",
        modelDescription: vehicle.modelDescription ?? "",
      },
      engineKilowatts: numberToString(vehicle.engineKilowatts),
      color: vehicle.color ?? "",
      manufacturerColor: vehicle.manufacturerColor ?? "",
      vatDeductible: valuation.vatDeductible ?? vehicle.vatDeductible ?? false,
      reimport: valuation.reimport ?? vehicle.reimport ?? false,
      numberOfDoors: numberToString(vehicle.numberDoors),
      registrationDate: apiDateToInputString(valuation.registrationDateUsed ?? vehicle.registrationDate),
      numberOfHolders: numberToString(valuation.numberOfHoldersUsed ?? vehicle.numberOfHolders),
      usage: vehicle.vehicleUsage ?? "",
      currentMileage: numberToString(valuation.mileageUsed ?? vehicle.mileage),
      huDate: apiDateToInputString(vehicle.huDate),
      serviceHistory: vehicle.serviceHistory ?? "",
      accident: {
        state: valuation.accidentState ?? vehicle.accidentState ?? VehicleAccidentState.UNKNOWN,
        description: valuation.accidentDescription ?? vehicle.accidentDescription ?? "",
        value: numberToString(valuation.accidentValue ?? vehicle.accidentValue ?? 0),
      },
      exchangeEngineInstalled: Boolean(exchangeEngine),
      exchangeEngine: {
        exchangeDate: apiDateToInputString(exchangeEngine?.exchangeDate),
        vehicleMileageAtExchange: numberToString(exchangeEngine?.vehicleMileageAtExchange),
        engineMileageAtExchange: numberToString(exchangeEngine?.engineMileageAtExchange),
        notes: exchangeEngine?.notes ?? "",
      },
    },
    purchase: {
      transferDate: "",
      price: "",
      mileageAtBuyIn: "",
      otherAgreements: "",
    },
  };
}

export function mapCustomerToVehicleBuyInForm(customer: VehicleBuyInCustomer): VehicleBuyInFormValues["customer"] {
  const billingAddress = customer.addresses.find((entry) => entry.type === CustomerAddressType.Billing)?.address;

  return {
    type: customer.type,
    companyName: customer.customerCompanyName ?? "",
    registrationNumber: customer.vatId ?? "",
    customerNumber: "",
    firstName: customer.firstName ?? "",
    lastName: customer.lastName ?? "",
    birthDate: apiDateToInputString(customer.birthDate),
    email: customer.email ?? "",
    phoneNumber: customer.mobile ?? customer.landline ?? "",
    address: {
      street: billingAddress?.street ?? "",
      houseNumber: billingAddress?.houseNumber ?? "",
      houseExtension: billingAddress?.houseExtension ?? "",
      postalCode: billingAddress?.postalCode ?? "",
      city: billingAddress?.city ?? "",
      country: billingAddress?.country_code ?? "",
    },
  };
}

function parseNumber(value: string): number {
  return Number(value.replace(",", "."));
}

function requiredInputDateToApi(value: string): string {
  const date = inputStringToDate(value);

  if (!date) {
    throw new Error(`Expected a valid date input, received: ${value}`);
  }

  return formatDateToApi(date);
}

function requiredYearMonth(value: string): string {
  const apiValue = monthInputStringToApi(value);
  if (!apiValue) {
    throw new Error(`Expected a valid year-month input, received: ${value}`);
  }

  return apiValue;
}

export function calculateVehicleBuyInVatAmount(price: string, vatDeductible: boolean): number {
  const parsedPrice = parseNumber(price);
  if (!vatDeductible || !Number.isFinite(parsedPrice) || parsedPrice <= 0) {
    return 0;
  }

  const netPrice = Math.round((parsedPrice / 1.19) * 100) / 100;
  return Math.round((parsedPrice - netPrice) * 100) / 100;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function getVehicleBuyInCustomerEmailError(
  email: string,
  signatureDevice: VehicleBuyInFormValues["signatureDevice"],
  employeeEmail: string,
) {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) {
    return signatureDevice === VehicleBuyInSignatureDevice.Company ? undefined : "common:FieldIsRequired";
  }
  if (!EMAIL_PATTERN.test(normalizedEmail)) {
    return "masterdata:EmailMustBeValid";
  }
  if (
    signatureDevice === VehicleBuyInSignatureDevice.Customer &&
    normalizedEmail === employeeEmail.trim().toLowerCase()
  ) {
    return "fab:CustomerEmailMustDifferFromEmployeeEmail";
  }
  return undefined;
}

export function getVehicleBuyInBranchDeviceError(signatureTabletEmail: string, employeeEmail: string) {
  const normalizedEmail = signatureTabletEmail.trim().toLowerCase();
  if (!EMAIL_PATTERN.test(normalizedEmail)) {
    return "fab:BranchSignatureTabletEmailRequired";
  }
  if (normalizedEmail === employeeEmail.trim().toLowerCase()) {
    return "fab:BranchSignatureTabletEmailMustDifferFromEmployeeEmail";
  }
  return undefined;
}

export function buildVehicleBuyInCreateBody(values: VehicleBuyInFormValues): VehicleBuyInCreateBody {
  const isPrivateCustomer = values.customer.type === CustomerType.Private;
  const customerNumber = values.customer.customerNumber.trim();
  if (!values.type) {
    throw new Error("Expected a vehicle buy-in type");
  }
  if (!values.signatureDevice) {
    throw new Error("Expected a signature device");
  }
  if (customerNumber && !isValidVehicleBuyInCustomerNumber(customerNumber)) {
    throw new Error("Expected a numeric customer number");
  }

  return {
    vehicleValuationUuid: values.vehicleValuationUuid,
    branchUuid: values.branchUuid,
    signatureDevice: values.signatureDevice,
    type: values.type,
    tradeInReference:
      values.type === VehicleBuyInType.TradeIn && values.tradeInReference.trim()
        ? values.tradeInReference.trim()
        : undefined,
    customer: {
      type: values.customer.type,
      customerNumber: customerNumber || undefined,
      firstName: values.customer.firstName,
      lastName: values.customer.lastName,
      birthDate: isPrivateCustomer ? requiredInputDateToApi(values.customer.birthDate) : undefined,
      companyName: isPrivateCustomer ? undefined : values.customer.companyName,
      registrationNumber:
        !isPrivateCustomer && values.customer.registrationNumber.trim()
          ? values.customer.registrationNumber
          : undefined,
      address: {
        ...values.customer.address,
        houseExtension: values.customer.address.houseExtension.trim() || undefined,
      },
      email: values.customer.email.trim() || undefined,
      phoneNumber: values.customer.phoneNumber,
    },
    purchase: {
      transferDate: requiredYearMonth(values.purchase.transferDate),
      price: parseNumber(values.purchase.price),
      mileageAtBuyIn: parseNumber(values.purchase.mileageAtBuyIn),
      otherAgreements: values.purchase.otherAgreements.trim() ? values.purchase.otherAgreements : undefined,
    },
  };
}

export function getBranchSnapshot(createData: CreateData, branchUuid: string) {
  return branchWithoutUuid(createData.branches.find((branch) => branch.uuid === branchUuid));
}
